import { useState, useCallback } from 'react';
import { UseColumnResizeReturn } from '../../types';

/**
 * Hook for managing table column resizing functionality
 *
 * This hook provides functionality to resize table columns dynamically,
 * maintaining width state and providing callbacks for resize events.
 *
 * @param initialWidths - Initial column widths as a record of columnId to width
 * @param onColumnResize - Optional callback when a column is resized
 *
 * @returns Object containing:
 *   - columnWidths: Current width state for all columns
 *   - handleColumnResize: Function to resize a specific column
 *   - getColumnWidth: Function to get the current width of a column
 *
 * @example
 * ```tsx
 * const { columnWidths, handleColumnResize, getColumnWidth } = useColumnResize(
 *   { name: 200, email: 150 },
 *   (columnId, width) => console.log(`Column ${columnId} resized to ${width}px`)
 * );
 *
 * // Resize a column
 * handleColumnResize('name', 250);
 *
 * // Get current width
 * const nameWidth = getColumnWidth('name');
 * ```
 */
export const useColumnResize = (
  initialWidths: Record<string, number> = {},
  onColumnResize?: (columnId: string, width: number) => void,
): UseColumnResizeReturn => {
  const [columnWidths, setColumnWidths] =
    useState<Record<string, number>>(initialWidths);

  const handleColumnResize = useCallback(
    (columnId: string, width: number) => {
      const newWidths = { ...columnWidths, [columnId]: width };
      setColumnWidths(newWidths);
      onColumnResize?.(columnId, width);
    },
    [columnWidths, onColumnResize],
  );

  const getColumnWidth = useCallback(
    (columnId: string) => columnWidths[columnId],
    [columnWidths],
  );

  return {
    columnWidths,
    handleColumnResize,
    getColumnWidth,
  };
};
